import * as React from 'react';
import HelpDocument from '../../models/helpDocument';
import * as styles from './report.module.scss';
import AltTextModal from './alt-text-modal';
import TableauButton from './tableau-button';
import IconButtonTooltip from '../common/iconButtonTooltip';
import TableauTool from '../../models/enums/tableauTool';
import { IconButton } from '@material-ui/core';
import { Help } from '@material-ui/icons';
import { datadogRum } from '@datadog/browser-rum';
import CustomViewsButton from './custom-views-button';

interface Props {
  documents: HelpDocument[];
  viz: any;
  container: any;
  isInteractive: boolean;
  reportName: string;
}

/**
 * @function ReportToolbar - Component for buttons for Tableau functions
 */
const ReportToolbar = ({ documents, viz, container, isInteractive, reportName }: Props) => {
  const [showModal, setShowModal] = React.useState(false);
  const [fullScreen, setFullScreen] = React.useState(false);
  const initialShowModal = React.useRef(true);
  const initialFullScreen = React.useRef(true);

  React.useEffect(() => {
    /** Adds event listener to detect full screen mode */
    window.addEventListener('fullscreenchange', () =>
      // @ts-ignore - webkitFullscreenElement for Edge only
      setFullScreen(!!document.fullscreenElement || !!document.webkitFullscreenElement)
    );
  }, []);

  const altHelpClick = () => {
    setShowModal((show) => !show);
    datadogRum.addUserAction('help', {
      user_action_details: {
        type: 'tableau_toolbar',
        name: 'help',
        modal_state: 'open',
        dashboard_name: reportName
      }
    });
  };

  React.useEffect(() => {
    if (initialShowModal.current) {
      initialShowModal.current = false;
    } else {
      if (showModal == false) {
        datadogRum.addUserAction('help', {
          user_action_details: {
            type: 'tableau_toolbar',
            name: 'help',
            modal_state: 'closed',
            dashboard_name: reportName
          }
        });
      }
    }
  }, [showModal]);

  React.useEffect(() => {
    if (initialFullScreen.current) {
      initialShowModal.current = false;
    } else {
      datadogRum.addUserAction('fullscreen_toggle', {
        user_action_details: {
          type: 'tableau_toolbar',
          name: 'fullscreen_toggle',
          isFullscreen: fullScreen,
          dashboard_name: reportName
        }
      });
    }
  }, [fullScreen]);

  return (
    <div className={fullScreen ? styles.toolbarFullscreen : styles.toolbar}>
      {showModal && <AltTextModal open={showModal} allDocuments={documents} onClose={(show) => setShowModal(show)} />}
      <div className={styles.leftTools}>
        <TableauButton viz={viz} dashboardName={reportName} type={TableauTool.Undo} edge="start" />
        <TableauButton viz={viz} dashboardName={reportName} type={TableauTool.Redo} />
        <TableauButton viz={viz} dashboardName={reportName} type={TableauTool.Revert} />
        <TableauButton viz={viz} dashboardName={reportName} type={TableauTool.Pause} />
      </div>
      <div className={styles.rightTools}>
        {documents.length > 0 && !fullScreen && (
          <IconButtonTooltip title="Help">
            <IconButton onClick={altHelpClick} className={styles.help} size="small">
              <Help />
            </IconButton>
          </IconButtonTooltip>
        )}
        {!fullScreen && (
          <CustomViewsButton viz={viz} dashboardName={reportName} isInteractive={isInteractive} edge={false} />
        )}
        {!fullScreen && <TableauButton viz={viz} dashboardName={reportName} type={TableauTool.Export} />}
        <TableauButton
          viz={viz}
          dashboardName={reportName}
          type={TableauTool.Maximize}
          fullScreen={fullScreen}
          container={container}
          edge="end"
        />
      </div>
    </div>
  );
};

export default ReportToolbar;
